import { syncApiInterceptor } from 'api/api-interceptor';
import { LOCALES, Locale } from 'config/constants/locales';
import { isSupportedLocale } from 'utils/is-supported-locale';
import { noop } from 'lodash';
import { useRouter } from 'next/router';
import React from 'react';
import { IntlProvider } from 'react-intl';
import { NEXT_DEFAULT_LOCALE } from 'utils/i18n/next-locales';
import { useConfig } from 'config/use-config';
import { useResetSwrOnLocaleChange } from 'hooks/common/use-reset-swr-on-locale-change';
import { useRegion } from 'hooks/use-region';
import * as translations from '../translations';

const messagesByLocale = {
  [LOCALES.CH]: translations.ch,
  [LOCALES.DE]: translations.de,
  [LOCALES.EN]: translations.en,
  [LOCALES.NL]: translations.nl,
};

const getActiveLocale = (routerLocale: string | undefined, defaultLocale: Locale): Locale => {
  if (!routerLocale || routerLocale === NEXT_DEFAULT_LOCALE) return defaultLocale;
  if (!isSupportedLocale(routerLocale)) return defaultLocale;

  return routerLocale;
};

const TranslationsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const region = useRegion();
  const { locale: defaultLocale } = useConfig();
  const { locale: routerLocale } = useRouter();
  const activeLocale = getActiveLocale(routerLocale, defaultLocale);

  // Keep the global API client in sync before children render/effects can issue requests.
  syncApiInterceptor(region, activeLocale);

  // Targets the default global cache — the SWRConfig in `_app` declares no `provider`.
  useResetSwrOnLocaleChange(activeLocale);

  return (
    <IntlProvider
      locale={activeLocale}
      messages={messagesByLocale[activeLocale] ?? messagesByLocale[defaultLocale]}
      onError={noop}
    >
      {children}
    </IntlProvider>
  );
};

export default TranslationsProvider;
